import { type ReactNode } from 'react';
import { useMemo } from 'react';
import * as React from 'react';
import Emoji from 'react-emoji-render';
import { rgba } from 'polished';
import styled from 'styled-components';

import { SIGNUP_LINK } from '../../utils';
import { Button } from '../design-system';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: ${({ theme }) => theme.maxWidth.BLOG};
  margin: 0 auto;
  width: 100%;
  padding: 25px;
  background: ${({ theme }) => rgba(theme.highlightColor, 0.025)};
  border: 1px solid ${({ theme }) => rgba(theme.highlightColor, 0.25)};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: ${({ theme }) => theme.lineHeight.DEFAULT};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    padding: 25px;
  }
`;

const Text = styled.div`
  margin-bottom: 25px;

  a {
    color: ${({ theme }) => theme.colors.WHITE};
  }
`;

const KickerButton = styled(Button)``;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    flex-direction: column;
    gap: 15px;
    width: 100%;

    ${KickerButton} {
      width: 100%;
    }
  }
`;

export interface ButtonOption {
  emoji?: string;
  link: string;
  text: ReactNode;
}

interface BlogKickerProps {
  btnstring?: string;
  buttons?: ButtonOption[];
  text?: string;
}

const DEFAULT_BUTTONS: ButtonOption[] = [
  {
    link: `${SIGNUP_LINK}?source=blog-kicker`,
    text: 'Get started for free',
    emoji: ':sparkles:'
  },
  {
    link: `/careers/`,
    text: 'Open roles',
    emoji: ':woman_technologist:'
  }
];

export const BlogKicker: React.ComponentType<BlogKickerProps> = React.memo(
  ({ buttons = DEFAULT_BUTTONS, text, btnstring }) => {
    const kickerText =
      text ||
      `This is something we think a lot about at Hex, where we're creating a platform that makes it easy to build and share interactive data products which can help teams be more impactful.<br /><br />If this is is interesting, click below to get started, or to check out opportunities to join our team.`;

    const buttonsToShow: ButtonOption[] = useMemo(() => {
      if (btnstring) {
        const split = btnstring.split('|');
        const buttonArr: ButtonOption[] = split.map(o => {
          const split = o.split(',');
          return {
            link: split[0].trim(),
            text: split[1].trim()
          };
        });
        return buttonArr;
      }
      return buttons;
    }, [buttons, btnstring]);

    return (
      <Wrapper>
        <Text dangerouslySetInnerHTML={{ __html: kickerText }} />
        <Buttons>
          {buttonsToShow.map((item, index) => {
            const text = item.emoji ? (
              <Emoji text={`${item.emoji} ${item.text}`} />
            ) : (
              item.text
            );
            return <KickerButton key={index} link={item.link} text={text} />;
          })}
        </Buttons>
      </Wrapper>
    );
  }
);
